import classNames from 'classnames';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { BotIcon } from '@/components/ui/bot-icon';
import { DemoPill, FilterSelect, Segmented, StateMessage } from '@/components/ui/controls';
import { Badge, PageHeader } from '@/components/ui/page';
import { btn, field, table } from '@/components/ui/styles';
import { formatDateTime, leadTitle } from '@/utils/format';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';

import { delivery, destination, removable, resendable, type BotAnswer, type Delivery } from './api-types';
import KnowledgeNav, { KnowledgeNote } from './knowledge-nav';

const DELIVERY: Record<Delivery, { label: string; tone: 'warn' | 'ok' | 'info' | 'danger' | 'neutral' }> = {
  waiting: { label: 'Waiting for a person', tone: 'warn' },
  sent: { label: 'Sent', tone: 'ok' },
  simulated: { label: 'Simulated', tone: 'info' },
  failed: { label: 'Not sent', tone: 'danger' },
  unsent: { label: 'Not sent yet', tone: 'warn' },
  none: { label: 'Nothing to send', tone: 'neutral' },
  removed: { label: 'Removed from the chat', tone: 'neutral' },
};

const DELIVERY_HINT: Partial<Record<Delivery, string>> = {
  simulated: 'Nothing left Prisma: Telegram is simulated, or email only goes to the log, until both are set up.',
  unsent: 'Its send never finished: send it again.',
  none: 'A demo answer with no chat or address to send to.',
  removed: 'A team member pressed 👎: the article it came from needs a better answer.',
};

// The lists: what still needs the team (a draft, or an answer nobody pressed 👍 or 👎 under), what stands, and what was removed.
type View = 'open' | 'confirmed' | 'removed' | 'all';
const IN_VIEW: Record<View, (a: BotAnswer) => boolean> = {
  open: (a) => a.status === 'waiting' || a.status === 'answered',
  confirmed: (a) => a.status === 'confirmed' || a.status === 'corrected',
  removed: (a) => a.status === 'removed',
  all: () => true,
};
const EMPTY: Record<View, string> = {
  open: 'Nothing to check. An answer appears here when someone asks something an article answers.',
  confirmed: 'No confirmed answers yet.',
  removed: 'No answer was removed.',
  all: 'The bot has not answered anyone yet.',
};

const channelName = (a: BotAnswer) => (a.channel === 'telegram' ? 'Telegram' : 'Email');

/**
 * Bot answers (REM-2, REM-3): the short answers the bot gave from the articles for
 * clients' questions (GET /knowledge/answers).
 * - On Telegram the bot answers at once, with 👍 and 👎 under it (Q19). A team member's
 *   👍 confirms it and 👎 removes it from the chat; the same can be done here. A removed
 *   answer points to the article to improve, or to a new one.
 * - By email the bot only drafts: a person confirms or corrects it, and only then is it
 *   sent. A failed send keeps the decision and can be sent again.
 * A phone shows the answers as cards, with the same actions (UI-1, UI-2).
 */
const AnswersPage = () => {
  const list = useApi<BotAnswer[]>('/knowledge/answers');
  const [status, setStatus] = useState<View>('open');
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const answers = list.data ?? [];
  const count = (v: View) => answers.filter(IN_VIEW[v]).length;
  const rows = answers.filter(IN_VIEW[status]);

  // Sending reaches a client, so a person says yes once more; an answer with nowhere to go is only recorded.
  const sure = (a: BotAnswer) => {
    const to = destination(a);
    return !to || window.confirm(`Send this answer to ${to} (${channelName(a)})?`);
  };
  const act = async (call: () => Promise<{ data: BotAnswer }>) => {
    setBusy(true);
    setProblem(null);
    try {
      const { data } = await call();
      setEditing(null);
      if (data.send_error) setProblem(`Saved, but it could not be sent: ${data.send_error}`);
    } catch (err: unknown) {
      const code = (err as { response?: { status?: number } }).response?.status;
      setProblem(code === 409 ? 'Someone already handled this answer.' : 'That did not work. Try again.');
    } finally {
      setBusy(false);
      list.reload();
    }
  };
  const path = (a: BotAnswer, what: string) => `/knowledge/answers/${encodeURIComponent(a.id)}/${what}`;
  const confirm = (a: BotAnswer) => sure(a) && act(() => http.post<BotAnswer>(path(a, 'confirm')));
  const correct = (a: BotAnswer) => sure(a) && act(() => http.post<BotAnswer>(path(a, 'correct'), { text: draft.trim() }));
  const sendAgain = (a: BotAnswer) => sure(a) && act(() => http.post<BotAnswer>(path(a, 'send')));
  // An answer given at once: confirm it (its buttons go), or take it out of the chat.
  const confirmGiven = (a: BotAnswer) => act(() => http.post<BotAnswer>(path(a, 'confirm')));
  const remove = (a: BotAnswer) =>
    window.confirm(`Remove this answer from ${destination(a) || 'the chat'}?`) && act(() => http.post<BotAnswer>(path(a, 'remove')));
  const changeStatus = (s: View) => {
    setStatus(s);
    setEditing(null);
  };

  // Where the answer goes; the same in the table and in the phone cards.
  const where = (a: BotAnswer) => {
    const to = destination(a);
    return to ? (
      <span className="[overflow-wrap:anywhere]">
        To {to} · {channelName(a)}
      </span>
    ) : (
      <span>{channelName(a)} · no destination</span>
    );
  };

  // The answer, or its correction editor.
  const answerBody = (a: BotAnswer) =>
    editing === a.id ? (
      <div className="space-y-2">
        <textarea className={field.textarea} rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} aria-label="Corrected answer" />
        <div className="flex flex-wrap gap-2">
          <button type="button" className={btn.primary} disabled={busy || !draft.trim()} onClick={() => correct(a)}>
            {destination(a) ? 'Correct and send' : 'Save correction'}
          </button>
          <button type="button" className={btn.quiet} onClick={() => setEditing(null)}>
            Cancel
          </button>
        </div>
      </div>
    ) : (
      <>
        <p className="flex gap-1.5 text-fg">
          <span className="mt-0.5 shrink-0">
            <BotIcon />
          </span>
          <span className={classNames(a.correction && 'text-fg-muted line-through')}>{a.answer}</span>
        </p>
        {a.correction && (
          <p className="mt-1.5 text-fg">
            <span className="text-xs text-fg-muted">Corrected to: </span>
            {a.correction}
          </p>
        )}
      </>
    );

  // What happened to it, and who decided: in Prisma (a name) or in the chat (a Telegram username).
  const state = (a: BotAnswer) => {
    const d = delivery(a);
    const who = a.decided_by || (a.decided_by_username && `@${a.decided_by_username}`);
    const verb = { corrected: 'Corrected', removed: 'Removed', confirmed: 'Confirmed' }[a.status as 'corrected' | 'removed' | 'confirmed'];
    return (
      <div className="space-y-1">
        <Badge tone={DELIVERY[d].tone} title={DELIVERY_HINT[d]}>
          {DELIVERY[d].label}
        </Badge>
        {verb && (who || a.decided_at) && (
          <p className="text-xs text-fg-faint">
            {verb}
            {who && ` by ${who}`}
            {a.decided_at && ` · ${formatDateTime(a.decided_at)}`}
          </p>
        )}
        {a.status === 'answered' && a.sent_at && <p className="text-xs text-fg-faint">Answered at once · nobody on the team pressed 👍 or 👎 yet</p>}
        {a.client_feedback && <p className="text-xs text-fg-muted">The client pressed {a.client_feedback === 'up' ? '👍' : '👎'}</p>}
        {d === 'failed' && <p className="text-xs text-danger [overflow-wrap:anywhere]">{a.send_error}</p>}
      </div>
    );
  };

  const actions = (a: BotAnswer) => {
    if (editing === a.id) return null;
    if (a.status === 'removed') {
      // The way to a better answer next time: the article it came from, or a new one for this question.
      return a.article_id ? (
        <Link to={`/knowledge/${encodeURIComponent(a.article_id)}`} className={btn.quiet}>
          Improve the article
        </Link>
      ) : (
        // Cut by characters, not UTF-16 units: half an emoji would make encodeURIComponent throw.
        <Link to={`/knowledge/new?title=${encodeURIComponent(Array.from(a.question).slice(0, 200).join(''))}`} className={btn.quiet}>
          Write an article
        </Link>
      );
    }
    if (a.status === 'answered') {
      return (
        <span className="inline-flex flex-wrap gap-1">
          {resendable(a) && (
            <button type="button" className={btn.quiet} disabled={busy} onClick={() => sendAgain(a)}>
              Send again
            </button>
          )}
          <button type="button" className={btn.quiet} disabled={busy} onClick={() => confirmGiven(a)}>
            👍 Confirm
          </button>
          {removable(a) && (
            <button type="button" className={btn.quiet} disabled={busy} onClick={() => remove(a)}>
              👎 Remove
            </button>
          )}
        </span>
      );
    }
    if (a.status !== 'waiting') {
      return (
        resendable(a) && (
          <button type="button" className={btn.quiet} disabled={busy} onClick={() => sendAgain(a)}>
            Send again
          </button>
        )
      );
    }
    const send = Boolean(destination(a));
    return (
      <span className="inline-flex flex-wrap gap-1">
        <button type="button" className={btn.quiet} disabled={busy} onClick={() => confirm(a)}>
          {send ? 'Confirm and send' : 'Confirm'}
        </button>
        <button
          type="button"
          className={btn.quiet}
          disabled={busy}
          onClick={() => {
            setEditing(a.id);
            setDraft(a.answer);
          }}
        >
          {send ? 'Correct and send' : 'Correct'}
        </button>
      </span>
    );
  };

  const sender = (a: BotAnswer) =>
    a.lead_id ? (
      <Link to={`/leads/${encodeURIComponent(a.lead_id)}`} className="[overflow-wrap:anywhere] hover:text-brand">
        {leadTitle('', a.lead_email)}
      </Link>
    ) : (
      'Unknown sender'
    );

  const article = (a: BotAnswer) =>
    a.article_id ? (
      <Link to={`/knowledge/${encodeURIComponent(a.article_id)}`} className="text-fg hover:text-brand">
        {a.article_title}
      </Link>
    ) : (
      <span className="text-fg-faint">None matched</span>
    );

  return (
    <div>
      <PageHeader
        title="Knowledge base"
        description="The bot's answers: at once on Telegram, to clients and the team, with 👍 and 👎 under them, and drafts by email that a person confirms."
      >
        <KnowledgeNav />
        <div className="mb-4 md:hidden">
          <FilterSelect label="Status" idle="open" value={status} onChange={(v) => changeStatus(v as View)}>
            <option value="open">To check ({count('open')})</option>
            <option value="confirmed">Confirmed ({count('confirmed')})</option>
            <option value="removed">Removed ({count('removed')})</option>
            <option value="all">All ({answers.length})</option>
          </FilterSelect>
        </div>
        <div className="mb-4 hidden md:block">
          <Segmented
            label="Status"
            value={status}
            onChange={changeStatus}
            options={[
              { value: 'open' as const, label: `To check (${count('open')})` },
              { value: 'confirmed' as const, label: `Confirmed (${count('confirmed')})` },
              { value: 'removed' as const, label: `Removed (${count('removed')})` },
              { value: 'all' as const, label: `All (${answers.length})` },
            ]}
          />
        </div>
      </PageHeader>
      <KnowledgeNote />
      {problem && <p className="mb-3 text-sm text-danger [overflow-wrap:anywhere]">{problem}</p>}

      {list.error && !list.data ? (
        <StateMessage title="Answers could not be loaded" tone="error">
          {list.error}
        </StateMessage>
      ) : !list.data ? (
        <div className="h-40 animate-pulse rounded-card bg-surface shadow-e1" />
      ) : rows.length === 0 ? (
        <StateMessage title="Nothing here">{EMPTY[status]}</StateMessage>
      ) : (
        <div className={table.wrap}>
          <ul className="md:hidden">
            {rows.map((a) => (
              <li key={a.id} className="space-y-2 border-b border-line px-4 py-3 text-sm last:border-0">
                <div>
                  <p className="text-fg-strong">{a.question}</p>
                  <p className="mt-0.5 text-xs text-fg-muted">
                    {sender(a)} · {formatDateTime(a.created_at)}
                  </p>
                  <p className="mt-0.5 text-xs text-fg-muted">{where(a)}</p>
                </div>
                {answerBody(a)}
                <p className="text-xs text-fg-muted">Article: {article(a)}</p>
                <div className="flex flex-wrap items-start gap-2">
                  {state(a)}
                  {a.demo && <DemoPill title="Made up by the local demo seed">Demo</DemoPill>}
                  <span className="ml-auto">{actions(a)}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="hidden overflow-x-auto md:block">
            <table className={classNames(table.table, 'min-w-[1080px]')}>
              <thead className={table.head}>
                <tr>
                  <th scope="col" className={table.th}>Question</th>
                  <th scope="col" className={table.th}>Goes to</th>
                  <th scope="col" className={table.th}>Answer</th>
                  <th scope="col" className={table.th}>Article</th>
                  <th scope="col" className={table.th}>Status</th>
                  <th scope="col" className={table.th}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} className={classNames(table.row, 'align-top')}>
                    <td className={classNames(table.td, 'w-[24%]')}>
                      <p className="text-fg-strong">{a.question}</p>
                      <p className="mt-0.5 text-xs text-fg-muted">
                        {sender(a)} · {formatDateTime(a.created_at)}
                      </p>
                      {a.demo && (
                        <span className="mt-1.5 inline-block">
                          <DemoPill title="Made up by the local demo seed">Demo</DemoPill>
                        </span>
                      )}
                    </td>
                    <td className={classNames(table.td, 'max-w-[180px] text-fg')}>{where(a)}</td>
                    <td className={classNames(table.td, 'w-[30%]')}>{answerBody(a)}</td>
                    <td className={table.td}>{article(a)}</td>
                    <td className={table.td}>{state(a)}</td>
                    <td className={classNames(table.td, 'text-right')}>{actions(a)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="border-t border-line px-4 py-3 text-sm text-fg-muted">
            {rows.length} of {answers.length} answers
          </p>
        </div>
      )}
    </div>
  );
};

export default AnswersPage;
